import type { ErrorState } from "../stores/CartNotificationStore";
import type { Money } from "../stores/BaseTypes";
import type { ArticlePrices } from "./Prices";
import { ItemGroup } from "../components/cart-page/ArticleList";
import { MovPerSupplierInfo } from "./CommonCartTypes";

export type ComparableItem = {
	readonly bundleId: string;
	readonly description: string;
	readonly addedTimestamp: number;
	readonly supplierId: string;
	readonly prices: ArticlePrices | null | undefined;
};
export type SortCriterion =
	| "SORT_BY_NAME_AZ"
	| "SORT_BY_NEWEST_FIRST"
	| "SORT_BY_OLDEST_FIRST"
	| "SORT_BY_DECREASING_PRICE"
	| "GROUP_BY_DELIVERY_MODE"
	| "GROUP_BY_CATEGORY"
	| "GROUP_BY_ADDER";
export const commonSortCriteria: Array<SortCriterion> = [
	"SORT_BY_NEWEST_FIRST",
	"SORT_BY_OLDEST_FIRST",
	"SORT_BY_NAME_AZ",
	"SORT_BY_DECREASING_PRICE",
];

const sortByNameAZ = (a: ComparableItem, b: ComparableItem): number => {
	const aDescription = a.description ? a.description : "";
	const bDescription = b.description ? b.description : "";
	return aDescription.localeCompare(bDescription);
};

const sortByNewestFirst = (a: ComparableItem, b: ComparableItem): number => {
	return b.addedTimestamp - a.addedTimestamp;
};

const sortByOldestFirst = (a: ComparableItem, b: ComparableItem): number => {
	return a.addedTimestamp - b.addedTimestamp;
};

// sorting by the total item net price (= bundlePrice * amount)
const comparePrice = (a: ComparableItem, b: ComparableItem): number => {
	const aPrice: Money | null | undefined = a.prices && a.prices.position.sum.net;
	const bPrice: Money | null | undefined = b.prices && b.prices.position.sum.net;
	return aPrice === bPrice ? 0 : aPrice == null ? -1 : bPrice == null ? 1 : aPrice.amount - bPrice.amount;
};

const sortByDecreasingPrice = (a: ComparableItem, b: ComparableItem): number => {
	return comparePrice(b, a);
};

const sortBySupplier = function (movPerSupplierInfo: MovPerSupplierInfo | undefined): Comparator<ComparableItem> {
	function noSupplierOf(item: ComparableItem): string {
		return item.supplierId ? "" : "z";
	}
	function warningsOf(item: ComparableItem): string {
		if (!item.supplierId) {
			return "";
		}
		return !(movPerSupplierInfo?.supplierToMovInfo[item.supplierId]?.sumIsAboveMov ?? true) ? "w" : "";
	}
	return (a: ComparableItem, b: ComparableItem): number => {
		const bComparator = `${noSupplierOf(b)}${warningsOf(b)}${b.supplierId}-${b.addedTimestamp}`;
		const aComparator = `${noSupplierOf(a)}${warningsOf(a)}${a.supplierId}-${a.addedTimestamp}`;
		return bComparator.localeCompare(aComparator);
	};
};

export type Comparator<T> = (arg0: T, arg1: T) => number;
export function composeSortFunctions<T>(...fs: Array<Comparator<T>>) {
	function composeTwoSortFunctions(f: Comparator<T>, g: Comparator<T>): Comparator<T> {
		return (x: T, y: T) => {
			const firstRes = f(x, y);

			if (firstRes === 0) {
				return g(x, y);
			}

			return firstRes;
		};
	}

	return fs.reduce(composeTwoSortFunctions);
}
export function errorsFirstSortFunction(cartErrorState: ErrorState | null | undefined): Comparator<ComparableItem> {
	function errorsOf(item: ComparableItem): number {
		return cartErrorState?.itemErrors.get(item.bundleId) ? 1 : 0;
	}

	return (x, y) => errorsOf(y) - errorsOf(x);
}

export function getSortFunctionErrorsFirst(
	cartErrorState: ErrorState | null | undefined,
	sortCriterion: SortCriterion,
	group: ItemGroup<ComparableItem>,
	movPerSupplierInfo: MovPerSupplierInfo | undefined,
) {
	const errorsFirst = errorsFirstSortFunction(cartErrorState);
	const byNameAZ = composeSortFunctions(errorsFirst, sortByNameAZ);
	const byNewest = composeSortFunctions(errorsFirst, sortByNewestFirst, sortByNameAZ);
	const byOldestFirst = composeSortFunctions(errorsFirst, sortByOldestFirst, sortByNameAZ);
	const byDecreasingPrice = composeSortFunctions(errorsFirst, sortByDecreasingPrice, sortByNameAZ);

	const sortFunctions: Record<SortCriterion, Comparator<ComparableItem>> = {
		SORT_BY_NAME_AZ: byNameAZ,
		SORT_BY_NEWEST_FIRST: byNewest,
		SORT_BY_OLDEST_FIRST: byOldestFirst,
		SORT_BY_DECREASING_PRICE: byDecreasingPrice,
		GROUP_BY_DELIVERY_MODE: group.name === "PARCEL_DELIVERY" ? sortBySupplier(movPerSupplierInfo) : byNewest,
		GROUP_BY_CATEGORY: byNameAZ,
		GROUP_BY_ADDER: byNameAZ,
	};

	return sortFunctions[sortCriterion];
}
